import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './LoyaltySignup.css';
import earlySignupImage from './hamilton-sign-up.png';
import signedUpImage from './new-dispensary-signed-up-01.png';
import analyticsService from './services/AnalyticsService.ts';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoyaltySignup = () => {
  const [searchParams] = useSearchParams();
  const showPhone = searchParams.get('showPhone') === 'true';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successType, setSuccessType] = useState<'new' | 'existing' | null>(null);
  const loadTimeRef = useRef(Date.now());

  useEffect(() => {
    analyticsService.trackPageView({
      page_title: 'Loyalty Program Signup',
      page_path: window.location.pathname,
      page_category: 'loyalty',
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedFirst || !trimmedLast || !trimmedEmail) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Timing check: submissions faster than 3s are likely bots
    if (Date.now() - loadTimeRef.current < 3000) {
      setSuccessType('new');
      return;
    }

    setIsSubmitting(true);

    try {
      const body: Record<string, string | number> = {
        firstName: trimmedFirst,
        lastName: trimmedLast,
        email: trimmedEmail,
        favoriteStoreID: 11,
        zipCode,
      };

      if (mobilePhone.trim()) {
        body.mobilePhone = mobilePhone.trim();
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/alpine/contacts`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();

      if (response.status === 201) {
        setSuccessType('new');
      } else if (response.status === 200 && data.message?.includes('already exists')) {
        setSuccessType('existing');
      } else if (response.status === 400) {
        setError(data.message || 'Validation failed. Please check your information.');
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } catch {
      setError('Unable to connect. Please check your internet connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (successType) {
    return (
      <div className="loyalty-signup-offset">
        <div className="loyalty-signup-container">
          <Helmet>
            <title>New Sweetspot Cannabis Dispensary Opening | Sign Up for Rewards</title>
            <meta name="description" content="Be the first to know when a new Sweetspot Cannabis dispensary opens near you. Sign up early for exclusive rewards and updates about our newest locations." />
            <meta name="keywords" content="new sweetspot dispensary, sweetspot new store, sweetspot cannabis opening, new dispensary near me, sweetspot dispensary coming soon, sweetspot rewards signup, new cannabis dispensary" />
            <meta name="robots" content="index, follow" />
            <link rel="canonical" href="https://sweetspotfarms.com/new-dispensary" />
            <meta property="og:title" content="New Sweetspot Cannabis Dispensary Opening | Sign Up for Rewards" />
            <meta property="og:description" content="Be the first to know when a new Sweetspot Cannabis dispensary opens near you. Sign up early for exclusive rewards and updates about our newest locations." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://sweetspotfarms.com/new-dispensary" />
          </Helmet>
          <div className="loyalty-signup-success">
            <img
              src={signedUpImage}
              alt="You're signed up! Keep an eye on your inbox, we'll be in touch soon."
              className="loyalty-signup-success-image"
            />
            {successType === 'existing' && (
              <p>Welcome back! We've updated your preferred store.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="loyalty-signup-offset">
      <div className="loyalty-signup-container">
        <Helmet>
          <title>New Sweetspot Cannabis Dispensary Opening | Sign Up for Rewards</title>
          <meta name="description" content="Be the first to know when a new Sweetspot Cannabis dispensary opens near you. Sign up early for exclusive rewards and updates about our newest locations." />
          <meta name="keywords" content="new sweetspot dispensary, sweetspot new store, sweetspot cannabis opening, new dispensary near me, sweetspot dispensary coming soon, sweetspot rewards signup, new cannabis dispensary" />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://sweetspotfarms.com/new-dispensary" />
          <meta property="og:title" content="New Sweetspot Cannabis Dispensary Opening | Sign Up for Rewards" />
          <meta property="og:description" content="Be the first to know when a new Sweetspot Cannabis dispensary opens near you. Sign up early for exclusive rewards and updates about our newest locations." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://sweetspotfarms.com/new-dispensary" />
        </Helmet>

        <img
          src={earlySignupImage}
          alt="Sweetspot Cannabis Dispensary new store opening promotion"
          className="loyalty-signup-hero"
        />

        {error && <div className="loyalty-signup-error">{error}</div>}

        <form className="loyalty-signup-form" onSubmit={handleSubmit} noValidate>
          <div className="loyalty-signup-row">
            <div className="loyalty-signup-field">
              <label htmlFor="firstName">First Name *</label>
              <input
                id="firstName"
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="loyalty-signup-field">
              <label htmlFor="lastName">Last Name *</label>
              <input
                id="lastName"
                type="text"
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="loyalty-signup-field">
            <label htmlFor="email">Email *</label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {showPhone && (
            <div className="loyalty-signup-field">
              <label htmlFor="mobilePhone">Mobile Phone</label>
              <input
                id="mobilePhone"
                type="tel"
                placeholder="(555) 555-5555"
                value={mobilePhone}
                onChange={(e) => setMobilePhone(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Honeypot field — hidden from real users */}
          <div className="loyalty-signup-honeypot" aria-hidden="true">
            <label htmlFor="zipCode">Zip Code</label>
            <input
              id="zipCode"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
            />
          </div>

          <label className="loyalty-signup-consent" htmlFor="consent">
            <input
              id="consent"
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              disabled={isSubmitting}
            />
            <span>
              Sweetspot may contact me with offers, news, and other information.
              I will be able to unsubscribe from the email list at any time by
              selecting &quot;unsubscribe&quot; at the bottom of any email.
              Must be 21+ to sign up.
            </span>
          </label>

          <button
            type="submit"
            className="loyalty-signup-submit"
            disabled={isSubmitting || !consentChecked}
          >
            {isSubmitting ? 'Signing Up...' : 'Sign Up'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoyaltySignup;
